"use client";

import { Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Faq() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const faqs = [{
    question: "What encryption standards do you use?",
    answer: "We utilize military-grade AES-256 encryption for all data tunnels. Our protocols ensure that your digital footprint remains invisible to ISPs, government surveillance, and malicious actors, maintaining complete sovereignty over your connection."
  }, {
    question: "How do I configure my devices?",
    answer: "Our platform offers one-click installation for Windows, macOS, iOS, and Android. Simply download the client from your dashboard, log in with your secure credentials, and select the 'Auto-Connect' feature to instantly shield your traffic."
  }, {
    question: "What information do you log?",
    answer: "VPNGENIX operates under a strict zero-logs policy. We do not track, store, or share your browsing history, connection timestamps, or IP addresses. Your privacy is our primary directive, verified by independent third-party audits."
  }, {
    question: "Can I access content globally?",
    answer: "Yes. With our global network of high-speed servers, you can bypass geo-restrictions and access your favorite content from anywhere in the world. Our smart-routing technology ensures the fastest possible connection speeds."
  }, {
    question: "How does the kill-switch work?",
    answer: "The integrated kill-switch monitors your connection status in real-time. If your VPN connection drops for any reason, the system instantly severs your internet access to prevent accidental data leaks, reconnecting only when the tunnel is secure."
  }, {
    question: "What is your refund policy?",
    answer: "We offer a 30-day money-back guarantee on all subscription plans. If you are not satisfied with the performance or security of our service, contact our support team within the first 30 days for a full, no-questions-asked refund."
  }];
  return <section data-section-id="2378" id="faq" className="dark bg-background py-24 md:py-32 lg:py-40 text-foreground">
      <div className="container mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 lg:gap-12">
          {}
          <div ref={leftRef} className={`lg:col-span-4 mb-16 lg:mb-0 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="lg:sticky lg:top-32">
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-8">
                — Protocol Knowledge Base
              </p>
              <h2 className="font-default text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight text-foreground mb-8">
                Operational<span className="italic font-normal"> Queries.</span>
              </h2>
              <p className="font-default text-base md:text-[17px] leading-relaxed text-muted-foreground mb-10 max-w-md">
                Essential technical insights to ensure your digital sovereignty 
                is maintained with precision. Most operational questions are 
                addressed below.
              </p>
              <p className="font-default text-base text-foreground">
                Need further assistance?{" "}
                <Link to="/VpnGenixPage" className="relative inline-block text-foreground group">
                  Contact our command center
                  <span className="absolute left-0 -bottom-0.5 h-px w-full bg-foreground origin-left scale-x-100 transition-transform duration-500 ease-out group-hover:scale-x-0 group-hover:origin-right" />
                  <span className="absolute left-0 -bottom-0.5 h-px w-full bg-foreground origin-right scale-x-0 transition-transform duration-500 ease-out delay-150 group-hover:scale-x-100 group-hover:origin-left" />
                </Link>
              </p>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-8 lg:col-start-6 transition-all duration-1000 ease-out delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-b border-border/60 last:border-b-0 group">
                  <AccordionTrigger className="py-8 hover:no-underline [&>svg]:hidden font-default text-xl md:text-[22px] font-semibold text-foreground leading-snug text-left items-center">
                    <span className="flex-1 pr-6">
                      <span className="italic">{faq.question.split(" ")[0]}</span>{" "}
                      {faq.question.split(" ").slice(1).join(" ")}
                    </span>
                    <span className="relative flex items-center justify-center size-6 shrink-0 transition-transform duration-500 ease-out group-data-[state=open]:rotate-[135deg]">
                      <Plus className="size-5 text-foreground" strokeWidth={1} />
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-8 pt-0">
                    <p className="font-default text-base md:text-[17px] leading-[1.7] text-muted-foreground max-w-2xl">
                      {faq.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}